import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import {
	type DismissCallback,
	useMessagePlacementHooks,
} from '@atlassian/post-office-context/message-placement';

import { usePostOfficeContextScope as useContextScope } from '@atlassian/post-office-context/post-office-context-scope';

import {
	asMessageRenderer,
	MaybeSuspenseBoundary,
} from '@post-office/placement-common/utils/message-renderer';
import type { InAppNarrowExposureExperiment } from '@post-office/message-template-contracts/message-template-placement-config';
import { useInitialData } from '@post-office/placement-contexts/hooks';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { EXPERIMENTAL_MESSAGES } from './experimental-messages-list';

import { useInjectedMessageTemplates } from './injected-message-templates-context';

import { MessageRenderer as DefaultMessageRenderer } from '../__generated__/client/render';

import { type ScreenSpaceFlagsMessageState } from '../api/types';

import type { Placement as SSFPlacement } from '../contracts/Placement';

import { scopes } from '../scopes';

import { usePostOfficeSearchParams } from './use-post-office-search-params';

import { type JSX } from 'react/jsx-runtime';

type PlacementMessageState = {
	messages?: ScreenSpaceFlagsMessageState[];
	triggeredMessages?: ScreenSpaceFlagsMessageState[];
};

type RankedMessage = ScreenSpaceFlagsMessageState & {
	inAppNarrowExposureExperiment?: InAppNarrowExposureExperiment;
};

// https://switcheroo.atlassian.com/ui/gates/5033fa11-c5cb-4d9a-8b6f-7feaa912386b/key/post-office_enable_narrow-exposure-classic
const NARROW_EXPOSURE_CLASSIC_GATE = 'post-office_enable_narrow-exposure-classic';
// https://switcheroo.atlassian.com/ui/gates/8fd2bfc0-9a54-4a07-b739-0b84c2e6ad44/key/post-office_enable_narrow-exposure-ssf-migration
const NARROW_EXPOSURE_SSF_MIGRATION_GATE = 'post-office_enable_narrow-exposure-ssf-migration';

/** Gives EXPERIMENTAL_MESSAGES entries their narrow exposure config, unless the server sent one. */
const withNarrowExposureConfig = (message: RankedMessage): RankedMessage => {
	if (message.inAppNarrowExposureExperiment) {
		return message;
	}
	const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
		(p) => p.messageTemplateId === message.messageTemplateId,
	);
	return experimentalMessage
		? { ...message, inAppNarrowExposureExperiment: experimentalMessage.narrowExposureExperiment }
		: message;
};

/**
 * Picks the first page-load message to render, checking EXPERIMENTAL_MESSAGES cohorts in rank order.
 */
const selectMessages = (
	messages: RankedMessage[],
	skipConfigCarryingMessages: boolean,
): RankedMessage[] => {
	const selectedMessages: RankedMessage[] = [];

	// New experiments in the screen-space-flags placement must be added to the EXPERIMENTAL_MESSAGES list
	for (const message of messages) {
		// The classic renderer hides config-carrying messages when its gate is off.
		if (skipConfigCarryingMessages && message.inAppNarrowExposureExperiment) {
			continue;
		}

		const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
			(p) => p.messageTemplateId === message.messageTemplateId,
		);

		if (experimentalMessage) {
			const cohort = FeatureGates.getExperimentValue<string>(
				experimentalMessage.statsigExperiment,
				experimentalMessage.statsigParameterName,
				'not-enrolled',
				{ fireExperimentExposure: true },
			);

			if (experimentalMessage.statsigAAExperiment) {
				FeatureGates.getExperimentValue<string>(
					experimentalMessage.statsigAAExperiment,
					experimentalMessage.statsigParameterName,
					'not-enrolled',
					{ fireExperimentExposure: true },
				);
			}

			if (experimentalMessage.statsigTestCohorts.includes(cohort)) {
				selectedMessages.push(message);
				break;
			}
		} else {
			selectedMessages.push(message);
			break;
		}
	}

	return selectedMessages;
};

export const ScreenSpaceFlagsPlacement = ({
	initialData,
	preloadedInitialData,
	setPreloadedInitialData,
	jiraFlagsService,
	reportExpectedError,
	reportUnexpectedError,
}: SSFPlacement & {
	initialData: Promise<Response | undefined> | undefined;
}): JSX.Element | null => {
	const injectedMessageTemplates = useInjectedMessageTemplates();

	// https://switcheroo.atlassian.com/ui/gates/3088e603-a723-449e-a763-b42e9db2a626/key/post-office_enable_ssf_injected_messages
	const isInjectedMessagesEnabled = FeatureGates.checkGate(
		'post-office_enable_ssf_injected_messages',
	);
	const injectedMessageRenderer = useMemo(
		() =>
			injectedMessageTemplates
				? asMessageRenderer(injectedMessageTemplates, 'screen-space-flags').MessageRenderer
				: DefaultMessageRenderer,
		[injectedMessageTemplates],
	);
	const MessageRenderer = isInjectedMessagesEnabled
		? injectedMessageRenderer
		: DefaultMessageRenderer;
	const isEPExternalMessageSSREnabled = FeatureGates.checkGate(
		'post-office_enabled_ep_external_message_ssr',
	);

	const isNarrowExposureClassicEnabled = FeatureGates.checkGate(NARROW_EXPOSURE_CLASSIC_GATE);
	const isRankedFallThroughEnabled = FeatureGates.checkGate(NARROW_EXPOSURE_SSF_MIGRATION_GATE);

	const [localMessageState, setLocalMessageState] = useState<PlacementMessageState>(() => {
		const messages = preloadedInitialData?.messages?.length
			? preloadedInitialData.messages
			: undefined;
		const triggeredMessages = preloadedInitialData?.triggeredMessages?.length
			? preloadedInitialData.triggeredMessages
			: undefined;
		return {
			messages,
			triggeredMessages,
		};
	});

	// Dismissals are stored locally to keep the local state relatively immutable,
	// except unless explicitly refreshed from the server.
	const [dismissedMessageInstanceIds, setDismissedMessageInstanceIds] = useState<string[]>([]);

	const filterByDismissed = useCallback(
		(list: ScreenSpaceFlagsMessageState[] | undefined) =>
			list?.filter((msg) => !dismissedMessageInstanceIds.includes(msg.messageInstanceId)),
		[dismissedMessageInstanceIds],
	);

	// Filter the messages at runtime to ensure they are always accurate and
	// so we don't need to manage the dismissed messages in the state.
	const { filteredMessages, filteredTriggeredMessages } = useMemo(() => {
		const { messages, triggeredMessages } = localMessageState;
		// Dismissing any ranked candidate consumes the whole response, as dismissing the single
		// rendered message did before, so preloaded data and auto-refetch see no remaining messages.
		const isResponseConsumed =
			isRankedFallThroughEnabled &&
			!!messages?.some((msg) => dismissedMessageInstanceIds.includes(msg.messageInstanceId));
		return {
			filteredMessages: isResponseConsumed ? [] : filterByDismissed(messages),
			filteredTriggeredMessages: filterByDismissed(triggeredMessages),
		};
	}, [
		localMessageState,
		filterByDismissed,
		isRankedFallThroughEnabled,
		dismissedMessageInstanceIds,
	]);

	const messagesToRender = useMemo(() => {
		if (!isRankedFallThroughEnabled) {
			return filteredMessages?.slice(0, 1);
		}

		const candidates = filteredMessages?.map(withNarrowExposureConfig);
		const firstMessage = candidates?.[0];
		// Transactional messages are not choreographed. Without an experiment, the first message is
		// guaranteed to show, so do not render the rest to minimise conflicting messages.
		return firstMessage?.messageCategory === 'Transactional' &&
			!firstMessage.inAppNarrowExposureExperiment
			? candidates?.slice(0, 1)
			: candidates;
	}, [isRankedFallThroughEnabled, filteredMessages]);

	useInitialData(initialData);

	const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

	const handleDismiss = useCallback<DismissCallback>(
		({ messageInstanceId }: { messageInstanceId: string }) => {
			if (messageInstanceId) {
				// dismiss the current message instance
				setDismissedMessageInstanceIds((current) => [...current, messageInstanceId]);
			} else {
				// eslint-disable-next-line no-console
				console.error(
					`A valid messageInstanceId is required to dismiss a message: ${messageInstanceId}`,
				);
			}
		},
		[],
	);

	const onDismissed = useCallback(
		(messageInstanceId: string) => {
			handleDismiss({ messageInstanceId });
		},
		[handleDismiss],
	);

	useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss]);
	useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked]);
	useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze]);

	useEffect(() => {
		// update the preloaded initial data with the accurate filtered messages
		if (typeof setPreloadedInitialData === 'function') {
			if (isEPExternalMessageSSREnabled) {
				setPreloadedInitialData({
					messages: filteredMessages ?? [],
					triggeredMessages: filteredTriggeredMessages ?? [],
				});
			} else {
				setPreloadedInitialData({
					messages: filteredMessages ?? [],
					triggeredMessages: filteredTriggeredMessages ?? [],
				});
			}
		}
	}, [
		filteredMessages,
		filteredTriggeredMessages,
		isEPExternalMessageSSREnabled,
		setPreloadedInitialData,
	]);

	const activeScopes = useContextScope({
		scopes,
		treatIntersectionAsSame: true,
	});

	const searchParams = usePostOfficeSearchParams();
	const messagesState = usePlacementApi<{
		messages: ScreenSpaceFlagsMessageState[];
		triggeredMessages?: ScreenSpaceFlagsMessageState[];
	}>('/update-messages', {
		queryParams: {
			...searchParams,
			updateKey: activeScopes.updateKey ?? '',
		},
		requestOptions: { method: 'POST', body: JSON.stringify(activeScopes) },
		throttleTime: 100,
		autoRefetch: {
			isEnabled: !!activeScopes.updateKey && typeof filteredMessages === 'undefined',
			updateKey: activeScopes.updateKey ?? '',
		},
	});

	useEffect(() => {
		const data = messagesState?.data;
		if (!data) return;

		const messages = data.messages ?? [];
		setLocalMessageState((prev) => ({
			...prev,
			// The shared renderer handles experiment evaluation when migration is enabled.
			messages: isRankedFallThroughEnabled
				? messages
				: selectMessages(messages, !isNarrowExposureClassicEnabled),
			triggeredMessages: data.triggeredMessages ?? [],
		}));
	}, [messagesState.data, isRankedFallThroughEnabled, isNarrowExposureClassicEnabled]);

	const content = FeatureGates.checkGate('post-office-screen-space-flags-shadow') ? (
		<>
			{messagesToRender?.map((msg) => (
				<MessageRenderer
					{...msg}
					key={msg.messageInstanceId}
					id={msg.messageInstanceId}
					messageInstanceId={msg.messageInstanceId}
					onDismissed={onDismissed}
					SuspenseBoundary={MaybeSuspenseBoundary}
					jiraFlagsService={jiraFlagsService}
					onError={reportUnexpectedError}
					reportExpectedError={reportExpectedError}
				/>
			))}
			{filteredTriggeredMessages?.map((msg) => (
				<MessageRenderer
					{...msg}
					key={msg.messageInstanceId}
					id={msg.messageInstanceId}
					messageInstanceId={msg.messageInstanceId}
					onDismissed={onDismissed}
					SuspenseBoundary={MaybeSuspenseBoundary}
					jiraFlagsService={jiraFlagsService}
					onError={reportUnexpectedError}
					reportExpectedError={reportExpectedError}
				/>
			))}
		</>
	) : null;

	return content;
};
